import { CompetitionTypeMultiSelect } from '../../components/competition-type-multi-select';
import { CategoryMultiSelect } from '~/pages/categories/components/category-multi-select';
import { useRegistrationCategories } from '../hooks/useRegistrationCategories';
import { useAsyncWeightClassesListQuery } from '~/pages/weight/mutations';
import { useRegistrationCategoryBulkMutation } from '../mutations';
import { RegistrationCategoryWriteRequest } from '~/apis/ssf';
import { ConfirmResetDialog } from './confirm-reset-dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '~/components/Button/Button';
import { useDialog } from '~/hooks/useDialog';
import { Grid } from '~/components/Grid/Grid';
import { useParams } from '~/hooks/useParams';
import { Fragment } from 'react/jsx-runtime';
import { Input } from '~/components/Input';
import { Resolver, useForm } from 'react-hook-form';
import { idParamSchema } from '~/schemas';
import { msg } from '~/utils/validation';
import { useEffect } from 'react';
import { SSF } from '~/apis/api';
import { uniqBy } from 'lodash';
import { z } from 'zod';

const schema = z.object({
  competition_type_ids: z.array(z.number()).min(1, { message: msg.required }),
  age_category_ids: z.array(z.number()).min(1, { message: msg.required }),
  no_weight_class: z.boolean().default(false),
});

type Schema = z.infer<typeof schema>;

export function RegistrationCategoriesForm() {
  const { categories, isLoading } = useRegistrationCategories();
  const { id } = useParams(idParamSchema.parse);

  const { formState, handleSubmit, register, setValue, watch, trigger, getValues } = useForm<Schema>({
    resolver: zodResolver(schema) as unknown as Resolver<Schema>,
    defaultValues: {
      competition_type_ids: [],
      age_category_ids: [],
      no_weight_class: false,
    },
  });

  const registrationCategoryBulkMutation = useRegistrationCategoryBulkMutation();
  const weightClassQuery = useAsyncWeightClassesListQuery();
  const confirmResetDialog = useDialog();

  async function onConfirm(payload: Schema) {
    const req: RegistrationCategoryWriteRequest[] = [];

    if (payload.no_weight_class) {
      for (const competitionTypeId of payload.competition_type_ids) {
        for (const ageCategoryId of payload.age_category_ids) {
          req.push({
            competition_type: competitionTypeId,
            weight_class: null,
            age_category: ageCategoryId,
            competition: id,
          });
        }
      }
    } else {
      const { data: competition } = await SSF.api.competitionsRetrieve(id);
      const weightClasses = await weightClassQuery.mutateAsync({
        competition_types: getValues('competition_type_ids'),
        date: competition.start_date ?? undefined,
      });

      for (const weightClass of weightClasses) {
        for (const competitionType of weightClass.competition_types) {
          for (const ageCategory of competitionType.age_categories.filter(ac =>
            payload.age_category_ids.includes(ac.id)
          )) {
            req.push({
              competition_type: competitionType.id,
              weight_class: weightClass.id,
              age_category: ageCategory.id,
              competition: id,
            });
          }
        }
      }
    }

    await registrationCategoryBulkMutation.mutateAsync({ id, payload: req });
    confirmResetDialog.onClose();
  }

  async function onSubmit(payload: Schema) {
    if (categories.length > 0) {
      confirmResetDialog.open();
      return;
    }

    await onConfirm(payload);
  }

  useEffect(() => {
    if (categories.length > 0) {
      const { competition_type_ids, age_category_ids } = getValues();

      if (!competition_type_ids.length) {
        setValue(
          'competition_type_ids',
          uniqBy(categories, c => c.competition_type.id).map(a => a.competition_type.id)
        );
      }

      if (!age_category_ids.length) {
        setValue(
          'age_category_ids',
          uniqBy(categories, c => c.age_category.id).map(a => a.age_category.id)
        );
      }

      // Restore checkbox state: if all categories have empty weight_classes, check the box
      const hasNoWeightClass = categories.every(c => c.weight_classes.length === 0);
      if (hasNoWeightClass) {
        setValue('no_weight_class', true);
      }
    }
  }, [categories, getValues, setValue]);

  return (
    <Fragment>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Grid style={{ rowGap: 0 }}>
          <Grid.Item cols={{ sm: 12, lg: 4 }}>
            <Input.Group formState={formState} htmlFor="competition_type_ids" label="Tävlingstyper">
              <CompetitionTypeMultiSelect
                {...register('competition_type_ids')}
                onChange={values => {
                  setValue('competition_type_ids', values);
                  setValue('age_category_ids', []);
                  trigger('competition_type_ids');
                }}
                value={watch('competition_type_ids')}
                formState={formState}
                disabled={isLoading}
              />
            </Input.Group>
          </Grid.Item>

          <Grid.Item cols={{ sm: 12, lg: 4 }}>
            <Input.Group formState={formState} htmlFor="age_category_ids" label="Ålderskategorier">
              <CategoryMultiSelect
                {...register('age_category_ids')}
                query={{ competition_types: watch('competition_type_ids') }}
                onChange={values => {
                  setValue('age_category_ids', values);
                  trigger('age_category_ids');
                }}
                value={watch('age_category_ids')}
                formState={formState}
                disabled={isLoading}
              />
            </Input.Group>
          </Grid.Item>

          <Grid.Item cols={{ sm: 12 }}>
            <Input.InlineGroup htmlFor="no_weight_class" label="Tillåt anmälan utan att ange viktklass">
              <Input.Checkbox {...register('no_weight_class')} />
            </Input.InlineGroup>
          </Grid.Item>
        </Grid>

        <Button type="submit" isLoading={weightClassQuery.isPending || registrationCategoryBulkMutation.isPending}>
          Hämta viktklasser
        </Button>
      </form>

      <ConfirmResetDialog
        isLoading={weightClassQuery.isPending || registrationCategoryBulkMutation.isPending}
        onConfirm={handleSubmit(onConfirm)}
        {...confirmResetDialog}
      />
    </Fragment>
  );
}
